import React, { SyntheticEvent } from 'react';

import axios from 'helpers/axios';
import config from 'helpers/config';

import WorkflowStatus from 'models/WorkflowStatusModel';
import RepairOrder from 'models/RepairOrderModel';
import { displayConfirmationModal } from 'helpers/services/removeConfirmationService';
import { displayWarningModal } from 'helpers/services/warningModalService';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { Edit, GripVertical, Info, Plus, Star, Trash2 } from 'lucide-react';

interface Props {}

interface State {
    workflowStatuses: WorkflowStatus[];

    isCreateNewWorkflowStatusActive: boolean;
    editableWorkflowStatusId: string | null;
    name: string | null;
    boardOrderIndex: number;
    activeRepairOrders: RepairOrder[];
}

class WorkflowSettings extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);

        this.state = {
            workflowStatuses: [],
            isCreateNewWorkflowStatusActive: false,
            editableWorkflowStatusId: null,
            name: null,
            boardOrderIndex: 0,
            activeRepairOrders: [],
        };

        this.getValue = this.getValue.bind(this);
        this.handleChange = this.handleChange.bind(this);
        this.createNew = this.createNew.bind(this);
        this.update = this.update.bind(this);
    }

    componentDidMount() {
        this.getWorkflowStatuses();
        this.getActiveRepairOrders();
    }

    getActiveRepairOrders() {
        axios
            .get(`${config.apiUrl}/repairOrder/board`)
            .then((res) => this.setState({ activeRepairOrders: res.data }))
            .catch((_) => console.log(_));
    }

    getWorkflowStatuses() {
        axios
            .get(`${config.apiUrl}/tenant/workflowStatuses`)
            .then((res) => this.setState({ workflowStatuses: res.data }));
    }

    getValue(name: string) {
        // @ts-ignore
        return this.state?.companySettings[name] || '';
    }

    handleChange(e: SyntheticEvent) {
        // @ts-ignore
        const { name, value } = e.target;
        // @ts-ignore
        this.setState({ [name]: value });
    }
    createNew() {
        const { name, boardOrderIndex } = this.state;

        if (!name?.trim()) {
            return;
        }

        const workflowStatus = {
            boardOrderIndex,
            name: name.trim(),
        };

        axios
            .post(`${config.apiUrl}/tenant/workflowStatuses`, workflowStatus)
            .then(() => {
                this.setState(
                    {
                        name: null,
                        boardOrderIndex: 0,
                        isCreateNewWorkflowStatusActive: false,
                    },
                    () => {
                        this.getWorkflowStatuses();
                    },
                );
            })
            .catch((error) => {
                console.error('Error creating workflow status:', error);
                // You could add a toast notification here
            });
    }

    update() {
        const { name, boardOrderIndex, editableWorkflowStatusId } = this.state;

        if (!name?.trim() || !editableWorkflowStatusId) {
            return;
        }

        const workflowStatus = {
            boardOrderIndex,
            name: name.trim(),
            id: editableWorkflowStatusId,
        };

        axios
            .put(`${config.apiUrl}/tenant/workflowStatus`, workflowStatus)
            .then(() => {
                this.setState(
                    {
                        name: null,
                        boardOrderIndex: 0,
                        isCreateNewWorkflowStatusActive: false,
                        editableWorkflowStatusId: null,
                    },
                    () => {
                        this.getWorkflowStatuses();
                    },
                );
            })
            .catch((error) => {
                console.error('Error updating workflow status:', error);
                // You could add a toast notification here
            });
    }
    handleDelete(workflowStatus: WorkflowStatus) {
        if (this.state.activeRepairOrders.find((RO) => RO.workflowStatusId === workflowStatus.id)) {
            displayWarningModal(
                'You cannot delete the workflow column which has active repair orders. Empty the column before deleting it.',
            );
        } else {
            displayConfirmationModal(`workflow status ${workflowStatus.name}`)
                .then(() => {
                    axios.delete(`${config.apiUrl}/tenant/workflowStatus/${workflowStatus.id}`).then(() => {
                        this.setState(
                            {
                                name: null,
                                boardOrderIndex: 0,
                                isCreateNewWorkflowStatusActive: false,
                                editableWorkflowStatusId: null,
                            },
                            () => {
                                this.getWorkflowStatuses();
                            },
                        );
                    });
                })
                .catch(() => {
                    // User cancelled deletion
                });
        }
    }
    onDragEnd = (result: any) => {
        if (!result.destination) {
            return;
        }

        const { workflowStatuses } = this.state;
        const reorderedStatuses = Array.from(workflowStatuses);
        const [reorderedItem] = reorderedStatuses.splice(result.source.index, 1);
        reorderedStatuses.splice(result.destination.index, 0, reorderedItem);

        // Update board order indices
        const updatedStatuses = reorderedStatuses.map((status, index) => ({
            ...status,
            boardOrderIndex: index,
        }));

        this.setState({ workflowStatuses: updatedStatuses });

        // Save the new order to the backend
        this.saveReorderedStatuses(updatedStatuses);
    };

    saveReorderedStatuses = (statuses: WorkflowStatus[]) => {
        const updatePromises = statuses.map((status) =>
            axios.put(`${config.apiUrl}/tenant/workflowStatus`, {
                id: status.id,
                name: status.name,
                boardOrderIndex: status.boardOrderIndex,
            }),
        );

        Promise.all(updatePromises)
            .then(() => {
                // Optionally show success message
                console.log('Workflow order updated successfully');
            })
            .catch((error) => {
                console.error('Error updating workflow order:', error);
                // Revert to original order on error
                this.getWorkflowStatuses();
            });
    };

    render() {
        const { workflowStatuses, isCreateNewWorkflowStatusActive, editableWorkflowStatusId, name } = this.state;

        return (
            <div className="flex flex-col min-h-screen bg-gray-50">
                {/* Header */}
                <div className="bg-white border-b border-gray-200 px-8 py-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <nav className="flex items-center space-x-2 text-sm text-gray-500 mb-2">
                                <span>Settings</span>
                                <span>/</span>
                                <span className="text-blue-600 font-semibold">Workflow Configuration</span>
                            </nav>
                            <h2 className="text-3xl font-bold text-gray-900">Workflow Configuration</h2>
                            <p className="text-gray-600 mt-1">Configure the workflow categories for repair orders</p>
                        </div>
                        <button
                            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-semibold flex items-center space-x-2 transition-colors"
                            onClick={() =>
                                this.setState({
                                    isCreateNewWorkflowStatusActive: !isCreateNewWorkflowStatusActive,
                                    editableWorkflowStatusId: null,
                                    name: '',
                                    boardOrderIndex: workflowStatuses.length,
                                })
                            }
                        >
                            <Plus className="h-4 w-4" />
                            <span>Add new</span>
                        </button>
                    </div>
                </div>

                <div className="flex-1 p-8">
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead>
                                    <tr className="bg-blue-500 text-white">
                                        <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider">
                                            Order
                                        </th>
                                        <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider">
                                            Title
                                        </th>
                                        <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider">
                                            Default
                                        </th>
                                        <th className="px-6 py-4 text-right text-xs font-semibold uppercase tracking-wider">
                                            Actions
                                        </th>
                                    </tr>
                                </thead>

                                <DragDropContext onDragEnd={this.onDragEnd}>
                                    <Droppable droppableId="workflow-statuses">
                                        {(provided) => (
                                            <tbody ref={provided.innerRef} {...provided.droppableProps}>
                                                {workflowStatuses
                                                    .sort((a, b) => a.boardOrderIndex - b.boardOrderIndex)
                                                    .map((workflowStatus, index) => {
                                                        const isEditing =
                                                            editableWorkflowStatusId === workflowStatus.id;

                                                        return (
                                                            <Draggable
                                                                draggableId={workflowStatus.id}
                                                                key={workflowStatus.id}
                                                                index={index}
                                                                isDragDisabled={isEditing}
                                                            >
                                                                {(provided, snapshot) => (
                                                                    <tr
                                                                        ref={provided.innerRef}
                                                                        {...provided.draggableProps}
                                                                        className={`${
                                                                            snapshot.isDragging
                                                                                ? 'bg-blue-50 shadow-lg'
                                                                                : 'bg-white hover:bg-gray-50'
                                                                        } border-b border-gray-200 transition-colors`}
                                                                    >
                                                                        {isEditing ? (
                                                                            <>
                                                                                <td className="px-6 py-4">
                                                                                    <div className="flex items-center space-x-3">
                                                                                        <div
                                                                                            {...provided.dragHandleProps}
                                                                                            className="cursor-grab active:cursor-grabbing"
                                                                                        >
                                                                                            <GripVertical className="w-4 h-4 text-gray-400" />
                                                                                        </div>
                                                                                        <span className="w-8 h-8 rounded-full bg-blue-100 text-sm font-medium flex items-center justify-center text-blue-700">
                                                                                            {
                                                                                                workflowStatus.boardOrderIndex
                                                                                            }
                                                                                        </span>
                                                                                    </div>
                                                                                </td>
                                                                                <td className="px-6 py-4">
                                                                                    <input
                                                                                        type="text"
                                                                                        name="name"
                                                                                        className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                                                        onChange={this.handleChange}
                                                                                        value={name || ''}
                                                                                        placeholder="Enter workflow status name"
                                                                                        autoFocus
                                                                                    />
                                                                                </td>
                                                                                <td className="px-6 py-4">
                                                                                    {this.state.boardOrderIndex ===
                                                                                        0 && (
                                                                                        <div className="flex items-center space-x-1">
                                                                                            <Star className="h-4 w-4 text-amber-500 fill-current" />
                                                                                            <span className="text-xs font-semibold text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full">
                                                                                                Default
                                                                                            </span>
                                                                                        </div>
                                                                                    )}
                                                                                </td>
                                                                                <td className="px-6 py-4 text-right">
                                                                                    <div className="flex justify-end space-x-2">
                                                                                        <button
                                                                                            onClick={() =>
                                                                                                this.setState({
                                                                                                    editableWorkflowStatusId:
                                                                                                        null,
                                                                                                    isCreateNewWorkflowStatusActive:
                                                                                                        false,
                                                                                                    name: null,
                                                                                                    boardOrderIndex: 0,
                                                                                                })
                                                                                            }
                                                                                            className="px-3 py-1 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded hover:bg-gray-200 transition-colors"
                                                                                        >
                                                                                            Cancel
                                                                                        </button>
                                                                                        <button
                                                                                            onClick={this.update}
                                                                                            disabled={!name?.trim()}
                                                                                            className="px-3 py-1 text-sm font-medium text-white bg-green-600 border border-green-600 rounded hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                                                                        >
                                                                                            Save
                                                                                        </button>
                                                                                    </div>
                                                                                </td>
                                                                            </>
                                                                        ) : (
                                                                            <>
                                                                                <td className="px-6 py-4">
                                                                                    <div className="flex items-center space-x-3">
                                                                                        <div
                                                                                            {...provided.dragHandleProps}
                                                                                            className="cursor-grab active:cursor-grabbing"
                                                                                        >
                                                                                            <GripVertical className="w-4 h-4 text-gray-400" />
                                                                                        </div>
                                                                                        <span className="w-8 h-8 rounded-full bg-blue-100 text-sm font-medium flex items-center justify-center text-blue-700">
                                                                                            {
                                                                                                workflowStatus.boardOrderIndex
                                                                                            }
                                                                                        </span>
                                                                                    </div>
                                                                                </td>
                                                                                <td className="px-6 py-4">
                                                                                    <div className="font-medium text-gray-900">
                                                                                        {workflowStatus.name}
                                                                                    </div>
                                                                                </td>
                                                                                <td className="px-6 py-4">
                                                                                    {workflowStatus.boardOrderIndex ===
                                                                                        0 && (
                                                                                        <div className="flex items-center space-x-1">
                                                                                            <Star className="h-4 w-4 text-amber-500 fill-current" />
                                                                                            <span className="text-xs font-semibold text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full">
                                                                                                Default
                                                                                            </span>
                                                                                        </div>
                                                                                    )}
                                                                                </td>
                                                                                <td className="px-6 py-4 text-right">
                                                                                    <div className="flex justify-end">
                                                                                        <button
                                                                                            onClick={() => {
                                                                                                this.setState({
                                                                                                    editableWorkflowStatusId:
                                                                                                        workflowStatus.id,
                                                                                                    name: workflowStatus.name,
                                                                                                    boardOrderIndex:
                                                                                                        workflowStatus.boardOrderIndex,
                                                                                                    isCreateNewWorkflowStatusActive:
                                                                                                        false,
                                                                                                });
                                                                                            }}
                                                                                            className="p-2 text-gray-500 hover:text-blue-600 transition-colors"
                                                                                            title="Edit"
                                                                                        >
                                                                                            <Edit className="w-4 h-4" />
                                                                                        </button>
                                                                                        <button
                                                                                            onClick={() =>
                                                                                                this.handleDelete(
                                                                                                    workflowStatus,
                                                                                                )
                                                                                            }
                                                                                            className="p-2 text-gray-500 hover:text-red-600 transition-colors"
                                                                                            title="Delete"
                                                                                        >
                                                                                            <Trash2 className="w-4 h-4" />
                                                                                        </button>
                                                                                    </div>
                                                                                </td>
                                                                            </>
                                                                        )}
                                                                    </tr>
                                                                )}
                                                            </Draggable>
                                                        );
                                                    })}

                                                {/* Add New Row */}
                                                {isCreateNewWorkflowStatusActive && (
                                                    <tr className="bg-blue-50 border-b border-gray-200">
                                                        <td className="px-6 py-4">
                                                            <div className="flex items-center space-x-2">
                                                                <GripVertical className="w-4 h-4 text-gray-300" />
                                                                <span className="w-8 h-8 rounded-full bg-blue-100 text-sm font-medium flex items-center justify-center text-blue-700">
                                                                    {this.state.boardOrderIndex}
                                                                </span>
                                                            </div>
                                                        </td>
                                                        <td className="px-6 py-4">
                                                            <input
                                                                type="text"
                                                                name="name"
                                                                className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                                onChange={this.handleChange}
                                                                value={name || ''}
                                                                placeholder="Enter workflow status name"
                                                                autoFocus
                                                            />
                                                        </td>
                                                        <td className="px-6 py-4">
                                                            {this.state.boardOrderIndex === 0 && (
                                                                <div className="flex items-center space-x-1">
                                                                    <Star className="h-4 w-4 text-amber-500 fill-current" />
                                                                    <span className="text-xs font-semibold text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full">
                                                                        Default
                                                                    </span>
                                                                </div>
                                                            )}
                                                        </td>
                                                        <td className="px-6 py-4 text-right">
                                                            <div className="flex justify-end space-x-2">
                                                                <button
                                                                    onClick={() =>
                                                                        this.setState({
                                                                            editableWorkflowStatusId: null,
                                                                            isCreateNewWorkflowStatusActive: false,
                                                                            name: null,
                                                                            boardOrderIndex: 0,
                                                                        })
                                                                    }
                                                                    className="px-3 py-1 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded hover:bg-gray-200 transition-colors"
                                                                >
                                                                    Cancel
                                                                </button>
                                                                <button
                                                                    onClick={this.createNew}
                                                                    disabled={!name?.trim()}
                                                                    className="px-3 py-1 text-sm font-medium text-white bg-green-600 border border-green-600 rounded hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                                                >
                                                                    Save
                                                                </button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                )}
                                                {provided.placeholder}
                                            </tbody>
                                        )}
                                    </Droppable>
                                </DragDropContext>
                            </table>
                        </div>
                    </div>

                    {/* Info Panel */}
                    <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
                        <div className="flex gap-4">
                            <Info className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" />
                            <div className="text-sm text-blue-800">
                                <h4 className="font-semibold mb-3 text-lg">How workflow categories work</h4>
                                <ul className="space-y-2 text-blue-700">
                                    <li className="flex items-start gap-2">
                                        <span className="w-1.5 h-1.5 bg-blue-600 rounded-full mt-2 flex-shrink-0"></span>
                                        Workflow categories appear as columns in your repair order board
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <span className="w-1.5 h-1.5 bg-blue-600 rounded-full mt-2 flex-shrink-0"></span>
                                        The order number determines the sequence of columns from left to right
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <span className="w-1.5 h-1.5 bg-blue-600 rounded-full mt-2 flex-shrink-0"></span>
                                        The default category is where new repair orders are automatically placed
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <span className="w-1.5 h-1.5 bg-blue-600 rounded-full mt-2 flex-shrink-0"></span>
                                        You can drag and drop orders between categories to track progress
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <span className="w-1.5 h-1.5 bg-blue-600 rounded-full mt-2 flex-shrink-0"></span>
                                        Use the drag handle (⋮⋮) to reorder workflow categories
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}

export default WorkflowSettings;
